import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useCurrency } from '../context/CurrencyContext';
import PaymentModal from './PaymentModal';
import { PayPalBadge } from './PayPalBadge';
import { trackBookingSubmitted } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ package: pkg, onClose }) => {
  const { formatPrice, currency, convertPrice } = useCurrency();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: 1,
    preferred_date: '',
    special_requests: '',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const isPerPerson = pkg.pricing_type === 'per_person';
  const guestsCount = parseInt(formData.guests) || 1;
  const totalAmount = isPerPerson ? pkg.price * guestsCount : pkg.price;

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      // Format as YYYY-MM-DD using LOCAL date parts (no UTC conversion).
      // Previously used toISOString() which shifts the date by IST offset
      // and could record the wrong day for users in India / east of UTC.
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      setFormData({ ...formData, preferred_date: dateStr });
    } else {
      setFormData({ ...formData, preferred_date: '' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('Please accept the Terms & Conditions and Cancellation Policy to proceed.');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        package_id: pkg.id,
        package_name: pkg.name,
        amount: totalAmount,
        currency: currency
      });
      
      setBookingData(response.data);
      setShowPayment(true);
      trackBookingSubmitted(response.data, totalAmount);
      toast.success('Booking created! Please complete payment to confirm.');
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    // Redirect to success page with booking ID
    window.location.href = `/booking-success?booking_id=${bookingData.id}`;
  };

  if (showPayment && bookingData) {
    return (
      <PaymentModal
        booking={bookingData}
        totalAmount={totalAmount}
        onClose={() => {
          setShowPayment(false);
          onClose();
        }}
        onSuccess={handlePaymentSuccess}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="booking-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-emerald-600 p-6 flex items-center justify-between border-b border-gold/20">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-white">Book Your Safari</h2>
            <p className="font-body text-sm text-white/80 mt-1">{pkg.name}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="close-modal-btn"
            className="text-white hover:text-amber-400 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="booking-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block font-body text-sm font-medium text-navy mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                data-testid="booking-name-input"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
              />
            </div>

            <div>
              <label htmlFor="email" className="block font-body text-sm font-medium text-navy mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="booking-email-input"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block font-body text-sm font-medium text-navy mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                data-testid="booking-phone-input"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
              />
            </div>

            <div>
              <label htmlFor="guests" className="block font-body text-sm font-medium text-navy mb-2">
                Number of Guests *
              </label>
              <input
                type="number"
                id="guests"
                name="guests"
                data-testid="booking-guests-input"
                required
                min="1"
                max={pkg.max_guests}
                value={formData.guests}
                onChange={handleChange}
                className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
              />
            </div>
          </div>

          <div>
            <label htmlFor="preferred_date" className="block font-body text-sm font-medium text-navy mb-2">
              Preferred Date *
            </label>
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              minDate={new Date()}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select your safari date"
              required
              data-testid="booking-date-input"
              className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
              wrapperClassName="w-full"
            />
          </div>

          <div>
            <label htmlFor="special_requests" className="block font-body text-sm font-medium text-navy mb-2">
              Special Requests (Optional)
            </label>
            <textarea
              id="special_requests"
              name="special_requests"
              data-testid="booking-requests-input"
              rows="3"
              value={formData.special_requests}
              onChange={handleChange}
              className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md px-4 py-3 font-body resize-none"
            ></textarea>
          </div>

          <div className="bg-sand p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-body text-navy uppercase tracking-wider text-sm block">Total Amount</span>
                <span className="font-body text-xs text-muted-foreground" data-testid="booking-price-breakdown">
                  {isPerPerson
                    ? `${formatPrice(pkg.price)} × ${guestsCount} guest${guestsCount > 1 ? 's' : ''}`
                    : `Fixed group price (up to ${pkg.max_guests} guests)`}
                </span>
              </div>
              <span className="price-luxe-bold text-3xl md:text-4xl text-emerald-700" data-testid="booking-total-amount">
                {formatPrice(totalAmount)}
              </span>
            </div>
            <p className="font-body text-xs text-muted-foreground mt-2">
              *Final amount may vary based on availability and season
            </p>
          </div>

          {/* PayPal trust card — boosts conversion */}
          <PayPalBadge variant="card" />

          {/* Terms acceptance — mandatory */}
          <label
            htmlFor="agree-terms"
            className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              agreedToTerms
                ? 'bg-emerald-50 border-emerald-500'
                : 'bg-white border-amber-300 hover:border-emerald-400'
            }`}
            data-testid="terms-acceptance-block"
          >
            <input
              type="checkbox"
              id="agree-terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 w-5 h-5 accent-emerald-600 cursor-pointer flex-shrink-0"
              data-testid="agree-terms-checkbox"
              required
            />
            <span className="font-body text-sm text-navy leading-relaxed">
              I have read and agree to the{' '}
              <Link
                to="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 underline font-semibold hover:text-emerald-900"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </Link>
              {' '}(including the Safari Allocation Policy) and the{' '}
              <Link
                to="/cancellation-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 underline font-semibold hover:text-emerald-900"
                onClick={(e) => e.stopPropagation()}
              >
                Cancellation Policy
              </Link>
              . I understand that wildlife sightings cannot be guaranteed and that JMD Wildlife
              decides the final safari zone, gate, and timing allotment.
            </span>
          </label>

          <button
            type="submit"
            data-testid="submit-booking-btn"
            disabled={isSubmitting || !agreedToTerms}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 rounded-full font-bold text-xl tracking-wide transition-all hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg border-4 border-emerald-400"
            style={{ backgroundColor: agreedToTerms ? '#059669' : '#9ca3af', color: '#ffffff' }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center space-x-2 text-white">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-white font-bold">Processing Booking...</span>
              </span>
            ) : (
              <span className="text-white font-bold text-xl">Proceed to Payment</span>
            )}
          </button>

          <p className="text-center font-body text-xs text-muted-foreground">
            Our team will contact you within 24 hours to confirm your booking
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
